import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams } from "react-router";
import {
	useElectricityMeterDetail,
	useHeatMeterDetail,
	useWaterMeterDetail,
} from "@/api/districts";
import { MeterAlertsTable } from "@/components/meters/meter-alerts-table";
import {
	MeterInfoCards,
	SourcesCard,
	StatCard,
	type TypedData,
} from "@/components/meters/meter-info-cards";
import { MeterReadingsCard } from "@/components/meters/meter-readings-card";
import { BackButton } from "@/components/navigation/back-button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { isValidLang, ROUTE_SLUGS } from "@/config/routes";
import { useHideElectricity } from "@/hooks/use-hide-electricity";

type MeterType = "woda" | "cieplo" | "energia";

const TYPE_BADGE_VARIANTS: Record<MeterType, "water" | "heat" | "energy"> = {
	woda: "water",
	cieplo: "heat",
	energia: "energy",
};

function MeterContent({
	sn,
	typed,
	isLoading,
}: {
	sn: string;
	typed: TypedData | undefined;
	isLoading: boolean;
}) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const meterType: MeterType = typed?.kind ?? "woda";
	const d = typed?.data;

	function goBack() {
		navigate(-1);
	}

	const typeLabel =
		meterType === "woda"
			? t("meterDetail.typeWater")
			: meterType === "cieplo"
				? t("meterDetail.typeHeat")
				: t("meterDetail.typeElectricity");

	const thirdStat = (() => {
		if (typed?.kind === "woda") {
			const batt = typed.data.remaining_battery_life_y;
			if (batt == null) return null;
			return {
				label: t("meterDetail.batteryLife"),
				value: `${batt} ${t("meterDetail.years")}`,
			};
		}
		if (typed?.kind === "energia") {
			const pwr = typed.data.last_power_consumption;
			return {
				label: t("meterDetail.powerDraw"),
				value: pwr != null ? `${pwr} W` : "—",
			};
		}
		return {
			label: t("meterDetail.apartment"),
			value: d?.apartment
				? `${d.apartment.street} ${d.apartment.apartment_number}`
				: "—",
		};
	})();

	const lastDataCard = (() => {
		if (isLoading) return <Skeleton className="h-40 w-full" />;
		if (!typed) return null;
		return <MeterReadingsCard readings={typed.data.last_readings} />;
	})();

	return (
		<div className="flex-1 min-h-0 overflow-y-auto">
			<div className="flex flex-col gap-6 p-4 lg:p-6">
				<div className="flex items-center gap-3">
					<BackButton onClick={goBack} />
					{isLoading ? (
						<Skeleton className="h-7 w-48" />
					) : (
						<>
							<h2 className="font-mono text-xl font-semibold">{sn}</h2>
							<Badge variant={TYPE_BADGE_VARIANTS[meterType]}>
								{typeLabel}
							</Badge>
						</>
					)}
				</div>

				{(isLoading || thirdStat !== null) && (
					<div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
						<StatCard
							label={thirdStat?.label ?? ""}
							value={thirdStat?.value ?? "—"}
							isLoading={isLoading}
						/>
					</div>
				)}

				<MeterInfoCards sn={sn} typed={typed} isLoading={isLoading} />

				{lastDataCard}

				{!isLoading && typed && <SourcesCard sources={typed.data.sources} />}

				{(meterType === "woda" || meterType === "cieplo") && (
					<div className="flex flex-col gap-3">
						<h3 className="text-base font-semibold">
							{t("meterDetail.alerts")}
						</h3>
						<MeterAlertsTable
							serialNumber={sn}
							type={meterType === "woda" ? "water" : "heat"}
						/>
					</div>
				)}
			</div>
		</div>
	);
}

function WaterMeterDetail({ sn }: { sn: string }) {
	const { data, isLoading } = useWaterMeterDetail(sn);
	return (
		<MeterContent
			sn={sn}
			typed={data ? { kind: "woda", data } : undefined}
			isLoading={isLoading}
		/>
	);
}

function HeatMeterDetail({ sn }: { sn: string }) {
	const { data, isLoading } = useHeatMeterDetail(sn);
	return (
		<MeterContent
			sn={sn}
			typed={data ? { kind: "cieplo", data } : undefined}
			isLoading={isLoading}
		/>
	);
}

function ElectricityMeterDetail({ sn }: { sn: string }) {
	const { data, isLoading } = useElectricityMeterDetail(sn);
	return (
		<MeterContent
			sn={sn}
			typed={data ? { kind: "energia", data } : undefined}
			isLoading={isLoading}
		/>
	);
}

export function MeterDetailPage() {
	const { type, sn, lang } = useParams<{
		type: string;
		sn: string;
		lang: string;
	}>();
	const currentLang = lang && isValidLang(lang) ? lang : "pl";
	const hideElectricity = useHideElectricity();
	if (!sn) return null;
	if (type === "woda") return <WaterMeterDetail sn={sn} />;
	if (type === "cieplo") return <HeatMeterDetail sn={sn} />;
	if (type === "energia") {
		if (hideElectricity)
			return (
				<Navigate
					to={`/${currentLang}/${ROUTE_SLUGS.waterMeters[currentLang]}`}
					replace
				/>
			);
		return <ElectricityMeterDetail sn={sn} />;
	}
	return null;
}
